import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, Alert, CircularProgress, Divider
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { agentsApi, handleApiError } from '../services/api';

/**
 * Modal for editing agent custom rules directly from the Review page.
 * Shows the source email text and lets the user edit custom rules.
 */
export default function AgentRulesModal({ open, onClose, sourceText, agentType = 'categorization', onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [customRules, setCustomRules] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    loadActiveAgent();
  }, [open, agentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadActiveAgent = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agentsApi.getAll();
      const allAgents = response.data.agents || [];

      // Find active custom agent for this type
      const activeCustom = allAgents.find(a =>
        a.type === agentType && !(a.isDefault || a.is_default) && (a.isActive || a.is_active)
      );

      if (activeCustom) {
        setAgent(activeCustom);
        setCustomRules(activeCustom.customRules || activeCustom.custom_rules || '');
      } else {
        // No custom agent — user will need to create one
        setAgent(null);
        setCustomRules('');
      }
    } catch (err) {
      setError(handleApiError(err, 'Failed to load agent'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndSave = async () => {
    try {
      setCreating(true);
      setError(null);

      // Find the default agent of this type to copy from
      const response = await agentsApi.getAll();
      const allAgents = response.data.agents || [];
      const defaultAgent = allAgents.find(a => a.type === agentType && (a.isDefault || a.is_default));

      if (!defaultAgent) {
        setError('No default agent found to copy from');
        return;
      }

      // Create custom agent
      const createResponse = await agentsApi.create({
        name: `My ${agentType.replace('_', ' ')} agent`,
        sourceAgentId: defaultAgent.id,
        customRules: customRules.trim() || null
      });

      const newAgent = createResponse.data.agent;

      // Activate it
      await agentsApi.activate(newAgent.id);

      // If rules were provided, update them
      if (customRules.trim()) {
        await agentsApi.update(newAgent.id, { customRules: customRules.trim() });
      }

      setAgent(newAgent);
      setSuccess('Custom agent created and activated!');
      onSuccess?.();
    } catch (err) {
      setError(handleApiError(err, 'Failed to create agent'));
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!agent) {
      await handleCreateAndSave();
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await agentsApi.update(agent.id, { customRules: customRules.trim() || null });
      setSuccess('Rules updated successfully');
      onSuccess?.();
    } catch (err) {
      setError(handleApiError(err, 'Failed to save rules'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon fontSize="small" />
        Customize Agent Rules
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

            {/* Source email text */}
            {sourceText && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Source Email Text
                </Typography>
                <TextField
                  fullWidth multiline minRows={4} maxRows={8}
                  value={sourceText}
                  InputProps={{ readOnly: true }}
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' },
                    '& .MuiOutlinedInput-root': { bgcolor: '#f5f5f5' }
                  }}
                />
                <Divider sx={{ mb: 2 }} />
              </>
            )}

            {/* Agent info */}
            {!agent && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No custom {agentType.replace('_', ' ')} agent exists yet. Add your rules below and
                a custom agent will be created and activated for you automatically.
              </Alert>
            )}

            {agent && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Editing rules for: <strong>{agent.name}</strong>
              </Typography>
            )}

            {/* Custom rules input */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Custom Rules
            </Typography>
            <TextField
              fullWidth multiline minRows={5} maxRows={12}
              value={customRules}
              onChange={(e) => setCustomRules(e.target.value)}
              placeholder={`Add rules to help the AI classify better, e.g.:\n- Emails containing 'Zerodha' or 'SIP' → Investments\n- Swiggy, Zomato → Eating out & delivery\n- Amazon Pay cashback → Refunds & cashback`}
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || creating || loading}
        >
          {saving || creating ? <CircularProgress size={20} /> : (agent ? 'Save Rules' : 'Create Agent & Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
